import React, { useState } from 'react';
import { PrimeIcons } from 'primereact/api';
import { confirmDialog } from 'primereact/confirmdialog';
import { isAfter } from 'date-fns';
import classNames from 'classnames';
import { mutate } from 'swr';
import { useAuthContext } from '../../../../context/AuthContext';
import { useClient } from '../../../../hooks/useClient';
import { EditExternalUserForm } from '../../../../components/EditExternalUserModal/EditExternalUserModal';
import { EXTERNAL_USER_STATUSES } from '../../../../types/externalUser';
import { RESERVATION_STATUSES } from '../../../../types/reservation';
import { ProfileOrganization } from '../types';
import { useProfileClient } from './useProfileClient';
import { useReservationsCalendar } from './useReservationsCalendar';

export const useClientActions = ({
  organization,
  externalUserId,
  user,
  reservations,
  mutateUser,
  mutateReservations,
}: {
  organization: ProfileOrganization;
  externalUserId: string | undefined;
  user: ReturnType<typeof useProfileClient>['user'];
  reservations: ReturnType<typeof useReservationsCalendar>['reservations'];
  mutateUser: ReturnType<typeof useProfileClient>['mutateUser'];
  mutateReservations: ReturnType<typeof useReservationsCalendar>['mutateReservations'];
}) => {
  const { patch } = useClient();
  const { user: authUser } = useAuthContext();

  const [isTogglingStatus, setIsTogglingStatus] = useState(false);

  const handleToggleStatus = async () => {
    if (!organization?.id || !externalUserId) return;

    setIsTogglingStatus(true);
    const isActive = user?.status === EXTERNAL_USER_STATUSES.ACTIVE;
    const action = isActive ? 'baja' : 'alta';

    await patch(`/external-users/${externalUserId}/toggle-status`, {
      successMessage: {
        summary: `Usuario dado de ${action}`,
        detail: `El usuario ha sido dado de ${action} correctamente`,
      },
      errorMessages: {
        summary: `Error al dar de ${action} al usuario`,
        defaultDetail: `No se ha podido dar de ${action} al usuario`,
      },
      handlers: {
        defaultSuccess: () => {
          // Actualizamos todos los datos del usuario
          mutateUser();
          mutateReservations();
          mutate(
            (key) =>
              typeof key === 'string' &&
              key.startsWith(`/organizations/${organization.id}/external-users/${externalUserId}/expenses`),
          );
        },
      },
    });

    setIsTogglingStatus(false);
  };

  const showToggleStatusDialog = () => {
    const isActive = user?.status === EXTERNAL_USER_STATUSES.ACTIVE;
    const action = isActive ? 'baja' : 'alta';
    const futureReservations = reservations?.filter(
      (reservation) =>
        reservation.externalUserStatus === RESERVATION_STATUSES.CONFIRMED &&
        isAfter(new Date(reservation.localStartDate), new Date()),
    );
    const queuedReservations = reservations?.filter(
      (reservation) => reservation.externalUserStatus === RESERVATION_STATUSES.IN_QUEUE,
    );
    const hasNextRoutine = user?.currentRoutine?.nextRoutine;
    const hasActiveCoupon = user?.userCoupons && user.userCoupons.some((_) => _.isActive && _.usesRemaining > 0);

    const hasAnyResources = futureReservations?.length || user?.currentRoutine || hasActiveCoupon || queuedReservations?.length;

    const futureReservationsText = futureReservations?.length ? (
      <>
        {futureReservations.length > 1
          ? `- Se cancelarán sus ${futureReservations.length} reservas`
          : '- Se cancelará su reserva'}
        .
        <br />
      </>
    ) : (
      ''
    );
    const queuedReservationsText = queuedReservations?.length ? (
      <>
        - Se le quitará de la cola de{' '}
        <b>{queuedReservations.length > 1 ? `${queuedReservations.length} reservas` : 'una reserva'}</b>.
        <br />
      </>
    ) : (
      ''
    );
    const routinesText = user?.currentRoutine ? (
      <>
        - Se <b>terminará su rutina</b> actual{hasNextRoutine ? ` y se borrará su próxima rutina` : ''}.<br />
      </>
    ) : (
      ''
    );
    const couponText =
      hasActiveCoupon && user?.userCoupons ? (
        <>
          - Se cancelarán todos sus bonos activos y se perderán sus usos restantes.
          <br />
        </>
      ) : (
        ''
      );

    const resourcesText = (
      <>
        {futureReservationsText}
        {queuedReservationsText}
        {routinesText}
        {couponText}
        {hasAnyResources ? (
          <>
            <br />
          </>
        ) : (
          <></>
        )}
      </>
    );

    confirmDialog({
      message: (
        <>
          ¿Estás seguro de que quieres dar de {action} a{' '}
          <b>
            {user?.name} {user?.surnames}
          </b>
          ?
          {isActive ? (
            <>
              <br />
              <br />
              {resourcesText}
              No podrá acceder a la aplicación hasta que se le dé de alta de nuevo.
            </>
          ) : (
            ''
          )}
        </>
      ),
      header: `Confirmar dar de ${action}`,
      icon: PrimeIcons.EXCLAMATION_CIRCLE,
      acceptLabel: `Sí, dar de ${action}`,
      rejectLabel: 'No, volver',
      accept: handleToggleStatus,
      acceptClassName: classNames('p-button-outlined', {
        'p-button-danger': isActive,
        'p-button-success': !isActive,
      }),
      defaultFocus: 'reject',
      draggable: false,
      resizable: false,
    });
  };

  /** Resolves `false` when the save failed, so the dialog stays open with what the user typed. */
  const handleSaveUser = async (updatedUser: EditExternalUserForm) => {
    if (!organization?.id || !externalUserId) return;
    let saved = false;
    await patch(`/external-users/${externalUserId}`, {
      body: updatedUser,
      successMessage: {
        summary: 'Usuario actualizado',
        detail: 'El usuario se ha actualizado correctamente',
      },
      errorMessages: {
        summary: 'Error al actualizar el usuario',
        defaultDetail: 'No se ha podido actualizar el usuario',
        USER_EMAIL_EXISTS: 'Ya existe un usuario con ese correo electrónico',
      },
      handlers: {
        defaultSuccess: () => {
          saved = true;
          mutateUser();
          mutate(`/users/${authUser?.id}/routine-alert`);
        },
      },
    });
    return saved;
  };

  const handleBulkCancelled = () => {
    mutateUser();
    mutateReservations();
    if (organization?.id) {
      mutate(
        (key) =>
          typeof key === 'string' &&
          (key.startsWith(`/organizations/${organization.id}/external-users/${externalUserId}/expenses`) ||
            key === `/organizations/${organization.id}/external-users/${externalUserId}/active-coupons` ||
            key.startsWith(`/organizations/${organization.id}/user-coupons`)),
      );
    }
  };

  return { isTogglingStatus, showToggleStatusDialog, handleSaveUser, handleBulkCancelled };
};
